import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CancellationPolicyRef } from '../../components/legal/CancellationPolicyRef';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../utils/api';
import { formatMoneyPrecise } from '../../utils/money';
import { PaymentPlanSchedule } from '../../components/payments/PaymentPlanSchedule';
import { formatDateLocale } from '../../utils/dateFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { CheckCircle, XCircle, Loader2, MapPin, Calendar, DollarSign, Building2, ArrowLeft, MessageSquare } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { TranslatedFromEnglishBadge } from '../../components/LanguageBadge';
import ProviderTaxBreakdown from '../../components/ProviderTaxBreakdown';
import { DateOfBirthGate } from '../../components/patient/DateOfBirthGate';
import { apiErrorCode, apiErrorMessage, ELIGIBILITY_CODES } from '../../utils/apiError';

const PatientQuoteView = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // P0-DOB-SELF-DECLARATION-01 — canonical gate evaluated BEFORE the patient
  // can click Accept, instead of letting the acceptance POST fail with 403.
  const [eligibility, setEligibility] = useState(null);

  const fetchEligibility = async () => {
    try {
      const { data } = await api.get('/patient/profile/eligibility');
      setEligibility(data);
      return data;
    } catch (e) {
      // Never block the page on the pre-check; the server-side gate on
      // acceptance stays authoritative.
      setEligibility(null);
      return null;
    }
  };

  useEffect(() => {
    fetchEligibility();
  }, []);

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/patient/quotes/detail/${quoteId}`);
      setQuote(response.data);
    } catch (error) {
      console.error('Error fetching quote:', error);
      toast({
        title: 'Error',
        description: 'Failed to load offer',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setActionLoading(true);
      const response = await api.post(`/patient/quotes/${quoteId}/accept`);
      
      toast({
        title: 'Treatment Reserved',
        description: 'Your offer is accepted. Next step: payment.'
      });
      // QOF-03 — hand the patient straight to the payment / next-step screen.
      const orderId = response.data?.order?.id;
      const qs = new URLSearchParams();
      qs.set('quote_id', quoteId);
      if (orderId) qs.set('order_id', orderId);
      navigate(`/patient/payment?${qs.toString()}`);
    } catch (error) {
      console.error('Error accepting quote:', error);
      const code = apiErrorCode(error);
      // The canonical eligibility gate answers with a structured, actionable
      // reason — surface it and reveal the inline completion form.
      if (ELIGIBILITY_CODES.includes(code)) {
        await fetchEligibility();
      }
      toast({
        title: 'Error',
        description: apiErrorMessage(error, t('patient.quote.acceptFailed')),
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);
      // Reason travels in the JSON body (query param kept server-side as a fallback).
      await api.post(`/patient/quotes/${quoteId}/reject`, { reason: rejectReason || '' });
      
      toast({
        title: 'Offer Rejected',
        description: 'The agent will follow up with you.'
      });
      
      setShowRejectDialog(false);
      setRejectReason('');
      
      // Refresh quote data
      await fetchQuote();
    } catch (error) {
      console.error('Error rejecting quote:', error);
      toast({
        title: 'Error',
        description: apiErrorMessage(error, t('patient.quote.rejectFailed')),
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'SENT': 'bg-blue-100 text-blue-800',
      'VIEWED': 'bg-purple-100 text-purple-800',
      'ACCEPTED': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'EXPIRED': 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const isExpired = () => {
    if (!quote?.valid_until) return false;
    return new Date(quote.valid_until) < new Date();
  };

  const canTakeAction = () => {
    if (!quote) return false;
    if (quote.quote_actionable !== undefined) return quote.quote_actionable; // LIFECYCLE-V3.2 server authority
    return ['SENT', 'VIEWED'].includes(quote.status) && !isExpired();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Alert variant="destructive">
          <AlertDescription>{t('patient.common.empty')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-12">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/patient/dashboard')} className="mb-2" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('adminNav.dashboard')}
          </Button>
          <div className="text-center">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              {t('patient.quote.title')}
            </h1>
            <div className="flex justify-center items-center gap-3 mt-3">
              <Badge className={getStatusColor(quote.status)}>{t(`patient.offers.status.${quote.status}`, { defaultValue: quote.status })}</Badge>
              {isExpired() && (
                <Badge variant="destructive">{t('patient.offers.expired')}</Badge>
              )}
              <TranslatedFromEnglishBadge currentLanguage={language} />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Clinic Information */}
        {quote.clinic_info && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {quote.clinic_info.clinic_name}
              </CardTitle>
              <CardDescription className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {quote.clinic_info.country}
                </span>
                {quote.clinic_info.rating && (
                  <span>⭐ {quote.clinic_info.rating}/5</span>
                )}
              </CardDescription>
            </CardHeader>
            {quote.clinic_info.description && (
              <CardContent>
                <p className="text-sm text-gray-600">{quote.clinic_info.description}</p>
              </CardContent>
            )}
          </Card>
        )}

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {t('patient.quote.package')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* TREATX-BATCH-10E — Total is the backend-authoritative
                `financial_summary.total` (base + Σ additional × qty).
                Prior rendering used `quote.base_price` directly which
                excluded additional services (Owner UAT: shown €5,555
                while canonical was €8,536). */}
            {(() => {
              const fs = quote.financial_summary || {};
              const currency = fs.currency || quote.currency || '';
              const sym = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency === 'GBP' ? '£' : currency === 'TRY' ? '₺' : `${currency} `;
              const total = Number.isFinite(fs.total)
                ? fs.total
                : (typeof quote.base_price === 'number' ? quote.base_price : Number(quote.base_price) || 0);
              const basePrice = Number.isFinite(fs.base_price)
                ? fs.base_price
                : (typeof quote.base_price === 'number' ? quote.base_price : Number(quote.base_price) || 0);
              const addTotal = Number.isFinite(fs.additional_services_total) ? fs.additional_services_total : 0;
              return (
                <div className="bg-blue-50 rounded-lg p-6 text-center" data-testid="quote-total-block">
                  <p className="text-sm text-gray-600 mb-2">{t('patient.offers.totalPrice')}</p>
                  <p className="text-4xl font-bold text-blue-600" data-testid="quote-total-amount">
                    {sym}{total.toLocaleString()} {currency}
                  </p>
                  {addTotal > 0 && (
                    <div className="mt-3 pt-3 border-t border-blue-200 text-xs text-blue-900/70 space-y-1 text-left inline-block">
                      <div className="flex justify-between gap-6" data-testid="quote-base-line">
                        <span>{t('patient.offers.basePrice', 'Base price')}</span>
                        <span className="font-medium">{sym}{basePrice.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between gap-6" data-testid="quote-additional-line">
                        <span>{t('patient.offers.additionalServicesLine', 'Additional services')}</span>
                        <span className="font-medium">{sym}{addTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-blue-200 text-left inline-block">
                    <ProviderTaxBreakdown
                      financialSummary={fs}
                      currencySym={sym}
                      testId="quote-provider-tax"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Included Services */}
            {quote.included_items && quote.included_items.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">{t('patient.offers.packageDetails')}</h3>
                <div className="space-y-2">
                  {quote.included_items.map((item, index) => (
                    item.included && (
                      <div key={index} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          {item.description && (
                            <p className="text-sm text-gray-600">{item.description}</p>
                          )}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* TREATX-BATCH-10E — Additional Services table (Qty × Unit = Line).
                Previously omitted from this surface; Owner UAT proved the
                patient couldn't see priced add-ons before Accept. */}
            {Array.isArray(quote.additional_services) && quote.additional_services.some(s => (s?.price ?? 0) > 0) && (
              <div data-testid="quote-additional-services">
                <h3 className="font-semibold mb-3">{t('patient.offers.additionalServices', 'Additional Services')}</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('patient.offers.tableService', 'Service')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.tableQty', 'Qty')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.tableUnitPrice', 'Unit Price')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('patient.offers.tableLineTotal', 'Line Total')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {quote.additional_services.filter(s => (s?.price ?? 0) > 0).map((svc, idx) => {
                        const qty = Number(svc.quantity) || 1;
                        const unit = Number(svc.price) || 0;
                        const line = unit * qty;
                        const cur = quote.currency || '';
                        const sym = cur === 'EUR' ? '€' : cur === 'USD' ? '$' : cur === 'GBP' ? '£' : cur === 'TRY' ? '₺' : `${cur} `;
                        return (
                          <tr key={idx} data-testid={`quote-additional-row-${idx}`}>
                            <td className="px-3 py-2 text-gray-800">{svc.name}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{qty}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{sym}{unit.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right font-medium">{sym}{line.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TREATX-BATCH-10E — Travel & Stay */}
            {(quote.hotel_nights || quote.hotel_type || quote.airport_transfer || quote.proposed_dates) && (
              <div data-testid="quote-travel-stay">
                <h3 className="font-semibold mb-2">{t('patient.offers.travelAndStay', 'Travel & Stay')}</h3>
                <div className="text-sm text-gray-700 space-y-1">
                  {quote.hotel_nights ? <p>{t('patient.offers.hotelNights', 'Hotel nights')}: <span className="font-medium">{quote.hotel_nights}</span></p> : null}
                  {quote.hotel_type ? <p>{t('patient.offers.hotelType', 'Hotel type')}: <span className="font-medium">{quote.hotel_type}</span></p> : null}
                  {quote.airport_transfer ? <p>{t('patient.offers.airportTransfer', 'Airport transfer')}: <span className="font-medium">{t('patient.offers.included', 'Included')}</span></p> : null}
                  {quote.proposed_dates ? <p>{t('patient.offers.proposedDates', 'Proposed dates')}: <span className="font-medium">{quote.proposed_dates}</span></p> : null}
                </div>
              </div>
            )}

            {/* TREATX-BATCH-10E — Payment Details from backend authority.
                Deposit / Balance / per-installment amount are the values
                the patient will actually pay; must be visible BEFORE
                Accept. */}
            {quote.deposit_amount != null && (() => {
              const fs = quote.financial_summary || {};
              const currency = fs.currency || quote.currency || '';
              return (
                <div data-testid="quote-payment-details">
                  <h3 className="font-semibold mb-2">{t('patient.offers.paymentDetails', 'Payment Details')}</h3>
                  {/* TREATX-PLAN-1 — the exact collections the patient has to
                      make, in order, from the single backend derivation. This
                      replaced "Balance … — N installments of X each", which
                      understated the final collection whenever the split was
                      not divisible. */}
                  <PaymentPlanSchedule
                    summary={fs}
                    currency={currency}
                    depositType={quote.deposit_type}
                    depositValue={quote.deposit_value}
                    testId="quote-payment-plan"
                  />
                </div>
              );
            })()}

            {/* TREATX-BATCH-10E — Terms & Conditions (only if populated) */}
            {quote.terms && String(quote.terms).trim() && (
              <div data-testid="quote-terms-and-conditions">
                <h3 className="font-semibold mb-2">{t('patient.offers.termsAndConditions', 'Terms & Conditions')}</h3>
                <p className="text-sm text-gray-700 whitespace-pre-line">{quote.terms}</p>
              </div>
            )}

            {/* TREATX-CANCELLATION-SSOT-CONSOLIDATION-01 — summary +
                deliberate full-document action from the immutable snapshot.
                The full legal body is NEVER dumped inline here. */}
            <div data-testid="quote-cancellation-policy">
              <h3 className="font-semibold mb-2">{t('patient.offers.cancellationPolicy', 'Cancellation & Refund Policy')}</h3>
              <CancellationPolicyRef
                snapshot={quote.cancellation_policy_snapshot}
                language={language}
                testId="quote-cancellation-policy-ref"
              />
            </div>
          </CardContent>
        </Card>

        {/* Validity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t('patient.quote.validity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              This quote is valid until:{' '}
              <span className="font-semibold">
                {/* TREATX-BATCH-B1.2 — canonical DD/MM/YYYY presentation. */}
                {quote.valid_until ? formatDateLocale(quote.valid_until) : 'N/A'}
              </span>
            </p>
            {isExpired() && (
              <Alert variant="destructive" className="mt-3">
                <AlertDescription>
                  This quote has expired. Please contact the agent for a new quote.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Agent Note */}
        {quote.notes_patient_visible && (
          <Card>
            <CardHeader>
              <CardTitle>{t('patient.offers.additionalInformation', 'Additional Information')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {quote.notes_patient_visible}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3" data-testid="quote-actions">
          {/* TREATX-PROVIDER-DOCUMENT-SHARING-12A — Quote → Messaging
              canonical deep-link. Closes the Finding D1 dead-end: the
              patient can continue the conversation in the SAME
              canonical thread without leaving the secure surface. We
              pass the clinic_id via react-router state so the existing
              auto-select-thread logic in PatientMessagesV2 picks the
              correct thread (no new thread is created). */}
          {quote.clinic_id && (
            <Button
              variant="default"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate('/patient/messages', { state: {
                clinicId: quote.clinic_id,
                // TREATX-BUG2-FIX (2026-02) — pass offer context so the
                // backend can auto-resolve case_id from quote → lead →
                // treatment_case linkage.
                contextType: 'offer',
                contextId: quote.id,
                subject: quote?.clinic_info?.clinic_name ? `About your quote — ${quote.clinic_info.clinic_name}` : 'About your quote',
              } })}
              data-testid="quote-continue-secure-conversation-btn"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('patient.dashboard.openMessages')}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              const API_URL = process.env.REACT_APP_BACKEND_URL;
              window.open(`${API_URL}/api/patient/quotes/${quote.id}/pdf?language=${language}`, '_blank');
            }}
            data-testid="download-pdf-btn"
          >
            <Calendar className="h-4 w-4 mr-2" />
            {t('patient.offers.downloadQuote', 'Download quote')}
          </Button>
        </div>

        {canTakeAction() && (
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardHeader>
              <CardTitle>{t('patient.quote.acceptAndContinue')}</CardTitle>
              <CardDescription>
                {t('patient.quote.reviewIntro')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {eligibility && eligibility.eligible === false && (
                <div className="mb-4">
                  <DateOfBirthGate
                    code={eligibility.code}
                    message={eligibility.message}
                    testIdPrefix="quote-dob"
                    onSaved={(next) => {
                      setEligibility(next);
                      if (next?.eligible) {
                        toast({
                          title: t('patient.dob.savedTitle'),
                          description: t('patient.dob.savedBody'),
                        });
                      }
                    }}
                  />
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  onClick={handleAccept}
                  disabled={actionLoading || (eligibility ? eligibility.eligible === false : false)}
                  className="flex-1"
                  size="lg"
                  data-testid="quote-accept-btn"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {t('patient.offers.acceptOffer')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={actionLoading}
                  className="flex-1"
                  size="lg"
                  data-testid="quote-reject-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  {t('patient.offers.reject')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status Messages */}
        {quote.status === 'ACCEPTED' && (
          <Alert data-testid="quote-accepted-banner">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              You have accepted this quote. An immutable canonical
              financial contract has been issued from this snapshot and a
              treatment order linked to it. The agent will contact you with
              next steps.
              {(quote.order_id || quote.financial_contract_id) && (
                <ul className="mt-2 ml-4 list-disc text-xs text-slate-600">
                  {quote.financial_contract_id && (
                    <li data-testid="quote-linked-contract-id">
                      Financial contract:{' '}
                      <span className="font-mono">
                        {quote.financial_contract_id}
                      </span>
                    </li>
                  )}
                  {quote.order_id && (
                    <li data-testid="quote-linked-order-id">
                      Order: <span className="font-mono">{quote.order_id}</span>
                    </li>
                  )}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}

        {quote.status === 'REJECTED' && (
          <Alert>
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              You have rejected this quote. The agent will follow up with you.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('patient.offers.declineOffer')}</DialogTitle>
            <DialogDescription>
              {t('patient.offers.declineConfirm')}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={t('patient.messages.typeMessage')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} data-testid="cancel-reject-btn">
              {t('patient.common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading} data-testid="confirm-reject-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('patient.offers.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientQuoteView;
